// dependencies
import {
	FloatingArrow,
	FloatingPortal,
	arrow,
	autoUpdate,
	flip,
	offset,
	safePolygon,
	shift,
	useClick,
	useFloating,
	useHover,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import React, { PropsWithChildren, useEffect, useRef, useState } from "react";

//utils
import { getColor } from "../../utils/getColor";

//components
import Text from "../Text";
//css
import css from "./Tooltip.module.scss";

const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 12;

interface BaseProps {
	/**
	 * Id of tooltip
	 */
	id?: string;
	/**
	 * Message of tooltip.
	 */
	message: string[] | string;
	/**
	 * Placement of tooltip.
	 * @default "top"
	 */
	placement?: "top" | "top-start" | "top-end" | "bottom" | "bottom-start" | "bottom-end" | "left" | "right";
	/**
	 * Size of tooltip.
	 * @default "large"
	 */
	size?: "large" | "small";
	/**
	 * For a more descriptive tooltip, you can add a title.
	 */
	title?: string;

	/**
	 * If false, the tooltip will be displayed.
	 */
	disabled?: boolean;

	/**
	 * If true, the tooltip will be delayed to a 0.5 second hover followed by 0.5 second fade in.
	 * if false, the tooltip will be displayed with no delay and a 0.5 second fade in.
	 *
	 * Reveal tooltips are delayed
	 * Instant tooltips are not delayed
	 */
	revealed?: boolean;

	/**
	 * Positioning strategy for Popper.js to use. Use "fixed" to position if is being clipped by parent container.
	 * https://popper.js.org/docs/v2/constructors/#strategy
	 * @default "absolute"
	 */
	strategy?: "absolute" | "fixed";

	/**
	 * maximum width to use for the tooltip
	 */
	maxWidth?: number;

	/**
	 * is the tooltip opened on click instead of on Hover
	 */
	openOnClick?: boolean;

	/**
	 * turn tooltip off after a certain amount of time in milliseconds
	 */
	turnOffAfter?: number | null;

	/**
	 * does the tooltip have an arrow
	 */
	hasArrow?: boolean;

	/**
	 * offset from the target element in pixels
	 * @default 8
	 */
	offsetSize?: number;

	weight?: "light" | "regular" | "semi-bold" | "bold";
	titleBottomLine?: boolean;
	/**
	 * Optional class name to add to the text element
	 */
	className?: string;
}

/**
 * A tooltip component that displays a message when the user hovers over an
 * element. This depends on the react-popper library.
 * @deprecated Use `Tooltip` from `@performyard/py-components` instead.
 */
const Tooltip = (props: PropsWithChildren<BaseProps>) => {
	const {
		id,
		children,
		message,
		placement = "top",
		size = "large",
		title,
		disabled = false,
		revealed = false,
		strategy = "absolute",
		maxWidth = 256,
		openOnClick = false,
		turnOffAfter = null,
		hasArrow = true,
		offsetSize = 8,
		weight,
		titleBottomLine = false,
		className = "",
	} = props;
	const arrowRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);

	// this hook handles the tooltip under the hood from the floating-ui library
	const { refs, floatingStyles, context } = useFloating({
		placement,
		strategy,
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(offsetSize), flip({ padding: 4 }), shift({ padding: 4 }), arrow({ element: arrowRef })],
		whileElementsMounted: autoUpdate,
	});

	// here are different ways to interact with the tooltip
	const hover = useHover(context, {
		move: false,
		enabled: !openOnClick,
		delay: revealed ? 500 : 0,
		handleClose: safePolygon({
			blockPointerEvents: false,
		}),
	});
	const click = useClick(context, { enabled: openOnClick });

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, click]);

	// here are transition styles for the tooltip
	const { isMounted, styles } = useTransitionStyles(context, {
		duration: { open: 500, close: 250 },
	});

	// this useEffect is for tooltips that need to stay open for a period then close on  their own
	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (turnOffAfter !== null && isOpen) {
			timeout = setTimeout(() => {
				setIsOpen(false);
			}, turnOffAfter);
		}
		return () => {
			clearTimeout(timeout);
		};
	}, [isOpen]);

	return (
		<div className={className} ref={refs.setReference} {...getReferenceProps()}>
			{children}
			{isMounted && !disabled && (
				<FloatingPortal>
					<div
						id={id}
						data-testid="tooltip"
						className={`${css.tooltip} ${size === "small" ? css.small : null}`}
						ref={refs.setFloating}
						style={{ ...floatingStyles, ...styles, maxWidth }}
						{...getFloatingProps()}
					>
						{title ? (
							<Text
								size={size === "large" ? "caption" : "extra-small"}
								weight={weight || "bold"}
								element="p"
							>
								{title}
							</Text>
						) : null}
						{titleBottomLine && <div className={css.line} />}
						{Array.isArray(message) ? (
							message.map((line, index) => (
								<Text
									key={`${line}${index}`}
									size={size === "large" ? "caption" : "extra-small"}
									weight="regular"
									element="p"
								>
									{line}
								</Text>
							))
						) : (
							<Text size={size === "large" ? "caption" : "extra-small"} weight="regular" element="p">
								{message}
							</Text>
						)}

						{hasArrow && (
							<FloatingArrow
								ref={arrowRef}
								context={context}
								fill={getColor("neutral", 7)}
								width={ARROW_WIDTH}
								height={ARROW_HEIGHT}
							/>
						)}
					</div>
				</FloatingPortal>
			)}
		</div>
	);
};

/** @deprecated Use `Tooltip` from `@performyard/py-components` instead. */
export default Tooltip;
